export const featurePath = '/v4/callcenter';

export const featureKeys = {
  all: [{ scope: 'callcenter-dashboard' }] as const,

  dashboard: () => [{ ...featureKeys.all[0], entity: 'dashboard' }] as const,

  agentStats: () => [{ ...featureKeys.all[0], entity: 'agent-stats' }] as const,
};

// fresh_fifo: never-called lead inside the SLA chase window (served
// oldest-first). fresh_lifo: never-called lead past the window (newest-first
// salvage). fresh_sla: the legacy single fresh bucket — still emitted for
// assignments created before the lane split, treated like fresh_fifo.
export type CallCenterBucket =
  | 'waiting'
  | 'fresh_fifo'
  | 'fresh_lifo'
  | 'fresh_sla'
  | 'callback'
  | 'reengagement';
export type AssignmentQueueState = 'offered' | 'accepted';

export type QueueLead = {
  lead_id: number;
  name: string;
  first_name: string;
  last_name: string;
  phone: string;
  city: string | null;
  state: string | null;
  status: string;
  franchise_id: number;
  waiting_since: string | null;
  followup_at: string | null; // date, not datetime
  created_at: string;
  // Triage detail, mirroring what the leads listing shows. All nullable: the
  // realtime broadcast payload is deliberately thinner than this, so a card
  // rendered from a broadcast has them absent until the next dashboard fetch.
  organization_name: string | null;
  email: string | null;
  zipcode: string | null;
  event_type_id: number | null;
  event_type_name: string | null;
  date_wanted: string | null;
  length_wanted: number | null;
  activity_interest: string[] | null;
  total: number | null;
  special_instructions: string | null;
  latest_message: string | null;
  latest_note: string | null;
  latest_note_at: string | null;
  updated_at: string | null;
};

export type QueueItemDial = {
  phone_e164: string | null;
  bp_service_name: string;
};

export type QueueItem = {
  assignment_id: number;
  state: AssignmentQueueState;
  bucket: CallCenterBucket;
  rotation_count: number;
  assigned_at: string | null;
  accepted_at: string | null;
  sla_deadline_at: string | null;
  // business-adjusted; null when clock paused (off-hours), already breached,
  // OR not yet started — sla_deadline_at is null while the holder is on a
  // call (the accept clock starts when they hang up).
  seconds_remaining: number | null;
  externally_held: boolean;
  lead: QueueLead | null;
  dial: QueueItemDial | null;
};

export type CallCenterAgentState = {
  user_id: number;
  bp_is_logged: boolean;
  bp_state: string | null;
  bp_reason: string | null;
  on_call: boolean;
  eligible: boolean;
  feed_paused: boolean;
  bp_synced_at: string | null;
  last_heartbeat_at: string | null;
};

export type CallCenterStats = {
  speed_to_lead_avg_seconds_today: number | null;
  leads_called_today: number;
  sla_hits_today: number;
  sla_breaches_today: number;
  calls_today: number;
  conversations_today: number;
  leads_completed_today: number;
  close_rate_30d: number | null;
};

export type CallCenterDashboardResponse = {
  agent: CallCenterAgentState;
  queue: QueueItem[];
  stats: CallCenterStats;
  meta?: Record<string, unknown>;
};

export type HeartbeatResponse = {
  feed_paused: boolean;
  server_time: string;
};

export type FeedPauseResponse = {
  feed_paused: boolean;
};

export type AgentStatsResponse = {
  stats: CallCenterStats;
  meta?: Record<string, unknown>;
};
